import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getUsersSummary } from "../../../../../api/api";
import { sendAgreementForApproval } from "../../../../../api/agreementsApi";
import { selectStyles } from "../../../../../utils/selectStyles";
import "./SendAgreementApprovalModal.css";

const SendAgreementApprovalModal = ({
  open,
  agreement,
  onClose,
  onSuccess,
}) => {
  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [comment, setComment] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelectedApprovers([]);
    setComment("");
    setError(null);
    setLoadingUsers(true);

    getUsersSummary({ isActive: true, pageSize: 1000 })
      .then((usersSummary) => {
        if (cancelled) return;
        const approvers = (usersSummary?.users || []).map((u) => ({
          value: u.id,
          label:
            [u.surname, u.name].filter(Boolean).join(" ").trim() ||
            `ID ${u.id}`,
        }));
        setApproverOptions(approvers);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Не удалось загрузить список сотрудников");
          setApproverOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open || !agreement) return null;

  const handleClose = () => {
    if (!sending) onClose();
  };

  const handleSubmit = async () => {
    const approverIds = selectedApprovers.map((a) => a.value);
    if (approverIds.length === 0) {
      setError("Выберите хотя бы одного согласующего");
      return;
    }

    setSending(true);
    setError(null);
    try {
      const created = await sendAgreementForApproval(agreement.id, {
        approver_ids: approverIds,
        comment: comment.trim() || undefined,
      });
      onSuccess?.(created);
      onClose();
    } catch (err) {
      setError(err?.message || "Не удалось отправить соглашение на согласование");
    } finally {
      setSending(false);
    }
  };

  const agreementLabel =
    agreement.number != null
      ? `№${agreement.number}`
      : `ID ${agreement.id}`;

  return (
    <div
      className="agreements-approval-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreements-send-approval-title"
    >
      <div
        className="agreements-approval-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div id="agreements-send-approval-title" className="agreements-approval-title">
          Отправка соглашения на согласование
        </div>
        <p className="agreements-approval-subtitle">
          Соглашение {agreementLabel}
        </p>

        <div className="agreements-approval-body">
          <label className="agreements-approval-label">
            Кому отправить на согласование:
          </label>
          <Select
            styles={{
              ...selectStyles,
              menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              menu: (base) => ({ ...base, zIndex: 2000 }),
            }}
            options={approverOptions}
            value={selectedApprovers}
            onChange={(opts) => setSelectedApprovers(opts || [])}
            placeholder={
              loadingUsers ? "Загрузка сотрудников..." : "Выберите согласующих"
            }
            isMulti
            isDisabled={loadingUsers || sending}
            className="agreements-approval-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />

          <label className="agreements-approval-label agreements-approval-label--mt">
            Комментарий (необязательно):
          </label>
          <textarea
            className="agreements-approval-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Добавьте комментарий для согласующих"
            rows={3}
            disabled={sending}
          />

          {error && (
            <div className="agreements-approval-error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="agreements-approval-actions">
          <button
            type="button"
            className="agreements-popup-btn agreements-popup-btn--cancel"
            onClick={handleClose}
            disabled={sending}
          >
            Отмена
          </button>
          <button
            type="button"
            className="agreements-popup-btn agreements-popup-btn--confirm"
            onClick={handleSubmit}
            disabled={sending || loadingUsers}
          >
            {sending ? "Отправка..." : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendAgreementApprovalModal;
